import { Spinner } from "@features/motion/Spinner"
import { cn, tv, VariantProps } from "@utils/tw"
import { Link } from "eddev/routing"
import { ComponentProps, forwardRef } from "react"

export type ButtonProps = ComponentProps<"button"> &
  VariantProps<typeof buttonStyles> & {
    href?: string | null
    target?: string
    rel?: string
  }

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, loading, disabled, active, ...props }, ref) => {
    const Comp = (props.href ? Link : "button") as "button"
    const isDisabled = disabled || loading
    const styles = buttonStyles({ variant, loading, active, className })

    return (
      <Comp
        {...props}
        ref={ref}
        className={cn(styles.base(), className)}
        type={props.href ? undefined : (props.type ?? "button")}
        disabled={props.href ? undefined : isDisabled}
        aria-disabled={props.href && isDisabled ? true : props["aria-disabled"]}
        aria-busy={loading || props["aria-busy"]}
        data-active={active ? "" : undefined}
        tabIndex={props.href && isDisabled ? -1 : props.tabIndex}
        onClick={(event) => {
          if (isDisabled) {
            event.preventDefault()
            return
          }
          props.onClick?.(event)
        }}
      >
        <span data-part="label" className={cn(styles.label())}>
          {props.children}
        </span>
        {loading ? (
          <Spinner
            data-part="spinner"
            aria-hidden="true"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          />
        ) : null}
      </Comp>
    )
  },
)
Button.displayName = "Button"

export const buttonStyles = tv({
  base: "type-label-s px-3 h-[3em] items-center w-fit flex gap-2 relative transition-all duration-300 rounded-none appearance-none text-blue text-center justify-center",
  variants: {
    variant: {
      filled: "bg-blue text-white decoration-transparent disabled:!bg-blue-light enabled:hover:bg-blue/90",
      outline:
        "bg-white border border-blue text-blue decoration-transparent hover:bg-blue-light/10 disabled:!bg-blue-light disabled:!border-blue-light",
      link: {
        base: "",
        label: "hover-underline",
      },
    },
    loading: {
      true: "pointer-events-none opacity-90 [&>[data-part=label]]:opacity-0",
    },
    active: {
      true: "",
      false: "",
    },
  },
  slots: {
    label: "",
  },
  defaultVariants: {
    variant: "link",
  },
})
